/**
 * Payout history — GET /api/tenant/gateway/payouts.
 *
 * Submitting a NEW withdrawal is owned by `gateway/components/WithdrawalForm.jsx` itself
 * (contract §9 pins its props to `{balance, minPayoutCents}` plus the injected-`gatewayApi`
 * DI rule, so it calls `gatewayApi.requestPayout` directly rather than through a hook this
 * file would have to also expose as extra, untyped props). This hook is purely the list +
 * reload half: `GatewayWallet` uses it for history, and passes its `reload` as
 * `WithdrawalForm`'s `onSubmitted` so a successful request refreshes the table beneath it.
 *
 * States:
 *  - loading: `payouts === null && !error`
 *  - error: `error !== null` (payouts keeps its last known value)
 *  - empty: `payouts` is `[]` — a real, distinct answer from "still loading"
 *
 * `gatewayApi` is an optional param defaulting to the module singleton, mirroring the
 * component-level DI rule (contract §9) so a consuming component that itself takes an
 * injected `gatewayApi` prop can thread it straight through.
 */
import { useCallback, useEffect, useState } from "react";
import defaultGatewayApi from "../api";

export function usePayoutRequest(gatewayApi = defaultGatewayApi) {
  const [payouts, setPayouts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await gatewayApi.listPayouts();
      setPayouts(data.rows || []);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [gatewayApi]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { payouts, loading, error, reload };
}

export default usePayoutRequest;
